import { tableCellStackNarrowClassName } from '@/components/table/table-cell-stack-narrow';
import { isWithinN } from '@airreading/common';
import { PlainSessionFeedback } from '@airreading/common/data';
import { cn } from '@/lib/tailwind-utils';

export interface SessionFeedbackStatusProps {
  feedbacks?: Pick<
    PlainSessionFeedback,
    'feedbackSubmitted' | 'lateFeedbackDismissed' | 'submittedAtInstant' | 'submittedHoursFromSessionEnd'
  >[];
  hoursCanceledBeforeStart: number | null;
  lessonShouldSkipFeedback?: boolean | null;
  /**
   * Override the UI label shown when feedback is submitted >= 24h after session end.
   * Defaults to the admin-facing label.
   */
  lateSentSuffixLabel?: string;
  testId?: string;
}

export const SessionFeedbackStatus = ({
  feedbacks,
  hoursCanceledBeforeStart,
  lessonShouldSkipFeedback,
  lateSentSuffixLabel = '(> 24H)',
  testId,
}: SessionFeedbackStatusProps) => {
  if (!feedbacks || feedbacks.length === 0) {
    return <span data-testid={testId}>-</span>;
  }
  if (lessonShouldSkipFeedback) {
    return <span data-testid={testId}>✅ Sent</span>;
  }

  const allSubmitted = feedbacks.length > 0 && feedbacks.every((f) => f.feedbackSubmitted);
  const allLateFeedbacksDismissed = feedbacks.every((f) => f.lateFeedbackDismissed === true);
  const isCanceledWithin12h = isWithinN(hoursCanceledBeforeStart, 12);

  if (allSubmitted || isCanceledWithin12h) {
    let moreThan24H = false;
    const validInstants = feedbacks.flatMap((f) =>
      f.submittedHoursFromSessionEnd ? [f.submittedHoursFromSessionEnd] : [],
    );
    const maxTimeBetweenSubmitAndEnd = Math.max(...validInstants);
    if (maxTimeBetweenSubmitAndEnd >= 24) {
      moreThan24H = true;
    }

    if (allLateFeedbacksDismissed) {
      moreThan24H = false; // If all late feedbacks are dismissed, we don't consider the 24H rule
    }

    return (
      <span className={cn('inline-flex flex-wrap items-center gap-x-1', tableCellStackNarrowClassName)}>
        <span data-testid={testId}>✅ Sent</span>
        {moreThan24H ? <span>{lateSentSuffixLabel}</span> : null}
      </span>
    );
  }

  return <span data-testid={testId}>-</span>;
};
